import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaTruck, FaExclamationTriangle } from 'react-icons/fa';
import DeliveryOTP from './DeliveryOTP';
import {
  LINEAR_FLOW,
  STATE_LABELS,
  getTrackingProgressPercent,
  resolveTrackingState,
  showCarrierFromState,
  timelineNodeStatus,
  isTerminalState,
  canRaiseClaim,
} from '../../utils/orderTrackingUtils';

function MiniTimeline({ currentState, compact }) {
  const states = isTerminalState(currentState)
    ? [...LINEAR_FLOW.slice(0, 3), currentState]
    : LINEAR_FLOW;

  return (
    <ol className={`space-y-0 ${compact ? 'flex flex-wrap gap-1.5' : 'space-y-3'}`}>
      {(compact ? [currentState] : states).map((stateKey) => {
        const status = timelineNodeStatus(stateKey, currentState);
        if (compact) {
          return (
            <li
              key={stateKey}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                status === 'current'
                  ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/50'
                  : 'bg-slate-800 text-slate-500'
              }`}
            >
              {status === 'current' && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              )}
              {STATE_LABELS[stateKey]?.split('—')[0]?.trim() || stateKey}
            </li>
          );
        }
        return (
          <li key={stateKey} className="flex items-start gap-3">
            <span
              className={`mt-1.5 h-3 w-3 shrink-0 rounded-full border-2 ${
                status === 'current'
                  ? 'border-amber-400 bg-amber-400 animate-pulse shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                  : status === 'done'
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-slate-600 bg-slate-800'
              }`}
            />
            <div>
              <p
                className={`text-sm font-medium ${
                  status === 'current' ? 'text-amber-300' : status === 'done' ? 'text-slate-200' : 'text-slate-500'
                }`}
              >
                {STATE_LABELS[stateKey] || stateKey}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * B-08 tracking summary for My Orders list
 */
export default function OrderTrackingSummary({ order, expanded = false }) {
  const navigate = useNavigate();
  const state = resolveTrackingState(order);
  const progress = getTrackingProgressPercent(state);
  const carrier = order.carrierTracking || {};
  const showCarrier = showCarrierFromState(state) && carrier.awb;

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-800/40 p-3 sm:p-4">
      {state === 'OUT_FOR_DELIVERY' && (
        <div className="mb-4">
          <DeliveryOTP orderId={order.id} />
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Live tracking</p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-amber-300">
            <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400 animate-pulse" />
            {STATE_LABELS[state] || state}
          </p>
          {!expanded && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
        <Link
          to={`/orders/${order.id}/track`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-slate-900 hover:bg-amber-400"
        >
          Track order <FaExternalLinkAlt className="text-[10px]" />
        </Link>
      </div>

      {canRaiseClaim(order) && (
        <button
          type="button"
          onClick={() =>
            navigate('/returns-disputes', { state: { tab: 'raise', orderId: order.id } })
          }
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-600/50 bg-amber-950/30 px-3 py-2 text-xs font-semibold text-amber-300 hover:bg-amber-950/50 sm:w-auto"
        >
          <FaExclamationTriangle className="text-[10px]" />
          Raise return / claim
        </button>
      )}

      {showCarrier && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
          <FaTruck className="text-blue-400" />
          <span>{carrier.carrierName || 'Carrier'}</span>
          <span className="font-mono text-slate-300">{carrier.awb}</span>
          {carrier.carrierLink && (
            <a
              href={carrier.carrierLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              Track shipment
            </a>
          )}
        </div>
      )}

      {order.reassignment && state === 'REASSIGNED' && (
        <p className="mt-2 text-xs text-blue-300">
          <span className="line-through text-slate-500">{order.reassignment.originalSellerCity}</span>
          {' → '}
          <strong>{order.reassignment.newSellerCity}</strong>
        </p>
      )}

      {expanded && (
        <div className="mt-4 border-t border-slate-700/50 pt-4">
          <MiniTimeline currentState={state} compact={false} />
        </div>
      )}
    </div>
  );
}
